import React from 'react';
import _ from "lodash";
import Select, { components as reactSelectComponents } from "react-select";
import { SingleDatePicker } from 'react-dates';
import "react-dates/initialize";
import 'react-dates/lib/css/_datepicker.css';
import { API } from "aws-amplify";
import { toast } from "react-toastify";
import { ContentState, Editor, EditorState, CompositeDecorator, Modifier } from "draft-js";
import Popup from "reactjs-popup";
import CreatableSelect from 'react-select/creatable';
import { validateItems, validateTotalSize } from "./qualifierValidation";

import InputCell from "@/common/form/InputCell";
import moment from '@/common/momentConfig';
import Modal from "@/common/Modal";
import LibraryPicker from "@/routes/library/LibraryPicker";
import { ENCOUNTER_TYPES, roles } from "@/utils/utils";
import EncounterTypePicker from "@/common/EncounterTypePicker";
import { COMPARISON_MAP } from "@/common/RuleComparisonConstants";
import EmailChips from "../../common/EmailChips";
import PhoneChips from "../../common/PhoneChips";

const MIN_DELAY_MINUTES = 5;

function ComparisonFieldValue({ onChange, type, qualifier, disabled, fieldName }) {
    const [focused, setFocused] = React.useState(false);
    const [setLoading, setSetLoading] = React.useState(false);
    const [fieldValues, setFieldValues] = React.useState(null);
    const [forceTextForSet, setForceTextForSet] = React.useState(false);
    const [inputValue, setInputValue] = React.useState('');
    const [timeUnit, setTimeUnit] = React.useState({ value: 'MINUTE', label: 'Minutes', multiplier: 60 });
    const [timeValue, setTimeValue] = React.useState(0);
    const [multiSelectValue, setMultiSelectValue] = React.useState([]);
    const keyCounter = React.useRef(0);

    const MINUTE_MULTIPLIER = 60;
    const HOUR_MINUTES = 3600;
    const WEEK_MINUTES = 604800;
    const DAY_MINUTES = 86400;

    const timeWindows = [
        { value: 'MINUTE', label: 'Minutes', multiplier: MINUTE_MULTIPLIER },
        { value: 'HOUR', label: 'Hours', multiplier: HOUR_MINUTES },
        { value: 'DAY', label: 'Days', multiplier: DAY_MINUTES },
        { value: 'WEEK', label: 'Weeks', multiplier: WEEK_MINUTES }
    ]

    const onInputChange = (e) => {
        onChange(e.target.value);
    }

    const onTimeValueChange = (e) => {
        setTimeValue(e.target.value);
        onChange(e.target.value * timeUnit.multiplier);
    }

    const onTimeUnitChange = (e) => {
        setTimeUnit(e);
        onChange(timeValue * e.multiplier);
    }

    const onDateChange = (newDate) => {
        onChange(newDate.format());
    }

    const handleChange = (value, actionMeta) => {
        if (value != null) {
            setMultiSelectValue(value);
        } else {
            setMultiSelectValue([]);
        }
    };
    const handleInputChange = (inputValue) => {
        setInputValue(inputValue);
    };
    const handleKeyDown = (event) => {
        if (!inputValue) return;

        switch (event.key) {
            case 'Enter':
            case 'Tab':
                event.preventDefault();

                // Parse and clean input items
                const rawItems = inputValue.split(/[,\n\r;]+/).map(s => s.trim()).filter(Boolean);
                if (!rawItems.length) return;

                // Get existing values
                const existingSet = new Set(multiSelectValue.map(v => v.value || v));

                // Deduplicate within input items
                const uniqueItems = [...new Set(rawItems)];

                // Filter out items that already exist
                const toBeAdded = uniqueItems.filter(item => !existingSet.has(item));

                // Calculate metrics
                const duplicatesRemoved = rawItems.length - uniqueItems.length;
                const alreadyExists = uniqueItems.length - toBeAdded.length;

                // Show info toast if duplicates or existing items
                if (duplicatesRemoved > 0 || alreadyExists > 0) {
                    let message = '';
                    if (duplicatesRemoved > 0) {
                        message += `Removed ${duplicatesRemoved} duplicate value(s). `;
                    }
                    if (alreadyExists > 0) {
                        message += `${alreadyExists} value(s) already in list. `;
                    }
                    message += `Added ${toBeAdded.length} unique value(s).`;

                    toast(message, {
                        type: 'info',
                        position: 'top-center',
                        autoClose: 5000
                    });
                }

                // Validate the unique items
                const validation = validateItems(multiSelectValue, toBeAdded);
                if (!validation.valid) {
                    toast(validation.message, { position: "top-center", type: "error" });
                    return;
                }

                // Add the unique items
                const newOpts = toBeAdded.map(item => createOption(item));
                setInputValue('');
                setMultiSelectValue([...multiSelectValue, ...newOpts]);
        }
    };
    const handlePaste = (event) => {
        event.preventDefault();
        const pastedText = event.clipboardData.getData("text");

        // Parse and clean pasted items
        const rawItems = pastedText.split(/[,\n\r;]+/).map(s => s.trim()).filter(Boolean);
        if (!rawItems.length) return;

        // Get existing values
        const existingSet = new Set(multiSelectValue.map(v => v.value || v));

        // Deduplicate within pasted items (preserve first occurrence using array from Set)
        const uniquePastedItems = [...new Set(rawItems)];

        // Filter out items that already exist in the list
        const toBeAdded = uniquePastedItems.filter(item => !existingSet.has(item));

        // Calculate metrics for user notification
        const duplicatesRemoved = rawItems.length - uniquePastedItems.length;
        const alreadyExists = uniquePastedItems.length - toBeAdded.length;

        // Show informational toast if duplicates were removed or items already exist
        if (duplicatesRemoved > 0 || alreadyExists > 0) {
            let message = '';
            if (duplicatesRemoved > 0) {
                message += `Removed ${duplicatesRemoved} duplicate value(s). `;
            }
            if (alreadyExists > 0) {
                message += `${alreadyExists} value(s) already in list. `;
            }
            message += `Added ${toBeAdded.length} unique value(s).`;

            toast(message, {
                type: 'info',
                position: 'top-center',
                autoClose: 5000
            });
        }

        // Validate the unique items (count and length limits)
        const validation = validateItems(multiSelectValue, toBeAdded);
        if (!validation.valid) {
            toast(validation.message, { position: "top-center", type: "error" });
            return;
        }

        // Add the unique items to the filter
        const newOpts = toBeAdded.map(item => createOption(item));
        setMultiSelectValue([...multiSelectValue, ...newOpts]);
    };
    const handlePasteRef = React.useRef(handlePaste);
    handlePasteRef.current = handlePaste;
    const multiSelectValueRef = React.useRef(multiSelectValue);
    multiSelectValueRef.current = multiSelectValue;

    const components = React.useMemo(() => ({
        DropdownIndicator: null,
        IndicatorsContainer: (props) => {
            const hasValues = multiSelectValueRef.current && multiSelectValueRef.current.length > 0;
            if (!hasValues) return null;
            const btnStyle = { cursor: "pointer", padding: 8, display: "flex", alignItems: "center", color: "inherit", opacity: 0.6 };
            return (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", alignSelf: "flex-start", paddingTop: 4, gap: 4 }}>
                    <div
                        title="Clear all values"
                        style={btnStyle}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            props.clearValue();
                        }}
                    >
                        <svg height="20" width="20" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"></path>
                        </svg>
                    </div>
                    <div
                        title="Copy all values"
                        style={btnStyle}
                        onClick={(e) => {
                            e.stopPropagation();
                            const values = multiSelectValueRef.current;
                            if (!values || !values.length) return;
                            const text = values.map(v => v.label).join(", ");
                            const textarea = document.createElement("textarea");
                            textarea.value = text;
                            textarea.style.position = "fixed";
                            textarea.style.opacity = "0";
                            document.body.appendChild(textarea);
                            textarea.select();
                            document.execCommand("copy");
                            document.body.removeChild(textarea);
                            toast("All values copied to clipboard", { position: "top-center", type: "info", autoClose: 1500 });
                        }}
                    >
                        <svg height="20" width="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                        </svg>
                    </div>
                </div>
            );
        },
        Input: (props) => (
            <reactSelectComponents.Input {...props} onPaste={(e) => handlePasteRef.current(e)} />
        ),
        MultiValueLabel: ({ data, ...props }) => {
            const label = data.label || "";
            const display = label.length > 20 ? label.substring(0, 20) + "..." : label;
            return (
                <div
                    title={label}
                    style={{ cursor: "pointer", color: "inherit", backgroundColor: "transparent", padding: "4px 8px" }}
                    onClick={(e) => {
                        e.stopPropagation();
                        const textarea = document.createElement("textarea");
                        textarea.value = label;
                        textarea.style.position = "fixed";
                        textarea.style.opacity = "0";
                        document.body.appendChild(textarea);
                        textarea.select();
                        document.execCommand("copy");
                        document.body.removeChild(textarea);
                        toast("Copied to clipboard", { position: "top-center", type: "info", autoClose: 1500 });
                    }}
                >
                    {display}
                </div>
            );
        },
    }), []);
    const selectStyles = React.useMemo(() => ({
        container: (base) => ({
            ...base,
            width: '100%',
            maxWidth: '100%',
            flex: 1,
        }),
        control: (base) => ({
            ...base,
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
        }),
        multiValue: (base) => ({
            ...base,
            minWidth: 0,
            maxWidth: '100%',
            margin: '3px 4px',
        }),
        multiValueLabel: (base) => ({
            ...base,
            padding: '4px 8px',
        }),
        multiValueRemove: (base) => ({
            ...base,
            padding: '0 4px',
        }),
        valueContainer: (base) => ({
            ...base,
            maxWidth: '100%',
            overflow: 'hidden',
            flexWrap: 'wrap',
            padding: '6px 8px',
        }),
        input: (base) => ({
            ...base,
            maxWidth: '100%',
            overflow: 'hidden',
            gridTemplateColumns: '0 minmax(0, 1fr)',
        }),
    }), []);
    const createOption = (label) => ({
        label,
        value: `${label}_${keyCounter.current++}`,
    });

    const joinValues = () => {
        let valuesOnly = [];
        for (let i = 0; i < multiSelectValue.length; i++) {
            valuesOnly.push(multiSelectValue[i].label);
        }
        qualifier.value = valuesOnly.join(',');
    }

    React.useEffect(() => {
        if (type == "SET" && !setLoading) {
            loadSchemaValues(fieldName);
        } else if (type == "AFTER" || type == "WITHIN_THE_LAST") {
            setTimeValAndUnit();
        }
    }, [type])

    React.useEffect(() => {
        setTimeValAndUnit();
        if (qualifier.value != null && qualifier.value.length > 0) {
            let str = qualifier.value;
            let res = str.split(",");
            let formatted = [];
            for (let i = 0; i < res.length; i++) {
                formatted.push(createOption(res[i]));
            }
            setMultiSelectValue(formatted);
        }
    }, [qualifier])

    React.useEffect(() => {
        joinValues();
    }, [multiSelectValue])

    const setTimeValAndUnit = () => {
        if (qualifier.value) {
            if (qualifier.value % WEEK_MINUTES == 0) {
                setTimeUnit(_.find(timeWindows, function (tw) { return tw.value == "WEEK"; }));
                setTimeValue(qualifier.value / WEEK_MINUTES)
            }
            else if (qualifier.value % DAY_MINUTES == 0) {
                setTimeUnit(_.find(timeWindows, function (tw) { return tw.value == "DAY"; }));
                setTimeValue(qualifier.value / DAY_MINUTES)
            }
            else if (qualifier.value % HOUR_MINUTES == 0) {
                setTimeUnit(_.find(timeWindows, function (tw) { return tw.value == "HOUR"; }));
                setTimeValue(qualifier.value / HOUR_MINUTES)
            }
            else if (qualifier.value % MINUTE_MULTIPLIER == 0) {
                setTimeUnit(_.find(timeWindows, function (tw) { return tw.value == "MINUTE"; }));
                setTimeValue(qualifier.value / MINUTE_MULTIPLIER)
            }
        }
    }

    const loadSchemaValues = (fieldName) => {
        setFieldValues(null);
        setSetLoading(true);
        API.get('Core', '/api/v1/encounter/valuesSet', {
            queryStringParameters: {
                field: fieldName
            }
        })
            .then(response => {
                setFieldValues(response.uniqueValues);
                setSetLoading(false);
            },
                error => {
                    setSetLoading(false);
                }
            );
    }

    const setSetFieldValue = (obj, action) => {
        switch (action.action) {
            case "select-option":
                onChange(obj.value);
                break;
        }
    }

    let genericInput = (
        <InputCell
            disabled={disabled}
            defaultValue={(qualifier.value ? qualifier.value : "")}
            required
            type={type == "NUMERIC" ? "number" : "text"}
            onChange={onInputChange}
            value={qualifier.value}
        />
    );

    if (qualifier.comparison !== "IN_LIST" && qualifier.comparison !== "NOT_IN_LIST" && qualifier.comparison !== "NOT_IN_LIST_CONTAINS" && qualifier.comparison !== "IN_LIST_CONTAINS") {
        switch (type) {
            case "DATE":
                if (qualifier.comparison === "IS_EMPTY" || qualifier.comparison === "IS_NOT_EMPTY") {
                    return '';
                } else if (qualifier.comparison === "AFTER" || qualifier.comparison === "WITHIN_THE_LAST") {
                    return (
                        <div>
                            <InputCell
                                disabled={disabled}
                                required
                                type={"number"}
                                defaultValue={(timeValue ? timeValue : "0")}
                                onChange={onTimeValueChange}
                                value={timeValue}
                            />
                            <Select className="tableCell selectCell" value={timeUnit} classNamePrefix="selectCell" isDisabled={disabled} onChange={onTimeUnitChange} options={timeWindows} />
                        </div>
                    );
                } else {
                    let exDate = moment()
                    if (qualifier.value && moment(qualifier.value).isValid())
                        exDate = moment(qualifier.value)

                    return (
                        <SingleDatePicker
                            date={exDate}
                            onDateChange={onDateChange}
                            focused={focused}
                            onFocusChange={({ focused }) => setFocused(focused)}
                            id="single_date_picker_rule"
                            isOutsideRange={() => false}
                            disabled={disabled}
                        />
                    );
                }

            case "SET":
                let toggle = (<a className="typeaheadSwitch"
                    onClick={() => setForceTextForSet(!forceTextForSet)}>{forceTextForSet ?
                        <div className="icon suggested" /> : <div className="icon custom" />}</a>);
                if (forceTextForSet) {
                    return (
                        <div className="fieldValueSelect">
                            {genericInput}
                            {toggle}
                        </div>
                    )
                }

                let optStrings = fieldValues ? fieldValues.concat("-") : ["-"];
                let opts = _.map(optStrings, function (o) {
                    return { label: o, value: o }
                });
                let val = qualifier.value ? qualifier.value : "-";
                let selectedVal = { label: val, value: val };
                return (
                    <div className="fieldValueSelect">
                        <Select className="selectCell" classNamePrefix="selectCell"
                            isDisabled={disabled}
                            isLoading={setLoading}
                            onChange={setSetFieldValue.bind(this)}
                            value={selectedVal}
                            options={opts}
                        />
                        {toggle}
                    </div>
                )

            case "NUMERIC":
            case "TEXT":
            default:
                return genericInput;
        }
    } else {
        return (
            <div className="fieldValueSelect expandableField">
                <CreatableSelect
                    className="tableCell selectCell inListSelect"
                    components={components}
                    styles={selectStyles}
                    inputValue={inputValue}
                    isClearable
                    isMulti
                    isDisabled={disabled}
                    menuIsOpen={false}
                    onChange={handleChange}
                    onInputChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    placeholder=""
                    value={multiSelectValue}
                />
            </div>
        )
    }
}

const EMPTY_VALUE = "-";
const MINUTE_MULTIPLIER = 60;
const HOUR_MINUTES = 3600;
const WEEK_MINUTES = 604800;
const DAY_MINUTES = 86400
const timeWindows = [
    { value: EMPTY_VALUE, label: "Immediate" },
    { value: 'MINUTE', label: 'Minutes', multiplier: MINUTE_MULTIPLIER },
    { value: 'HOUR', label: 'Hours', multiplier: HOUR_MINUTES },
    { value: 'DAY', label: 'Days', multiplier: DAY_MINUTES },
    { value: 'WEEK', label: 'Weeks', multiplier: WEEK_MINUTES }
]

const TYPE_OPTIONS = {
    EMPTY: {
        value: EMPTY_VALUE,
        label: EMPTY_VALUE,
        encounterTypes: ["ALL"]
    },
    SEND_SURVEY: {
        value: "SEND_SURVEY",
        label: "Send Survey",
        encounterTypes: ["ALL"]
    },
    SEND_TEXT: {
        value: "SEND_TEXT",
        label: "Send Text",
        encounterTypes: ["ALL"]
    },
    SEND_EMAIL: {
        value: "SEND_EMAIL",
        label: "Send Email",
        encounterTypes: ["ALL"]
    },
    CREATE_TASK: {
        value: "CREATE_TASK",
        label: "Create Task",
        encounterTypes: ["ALL"]
    },
    UPDATE_DISPOSITION: {
        value: "UPDATE_DISPOSITION",
        label: "Update Disposition",
        encounterTypes: [ENCOUNTER_TYPES.CONVERSATION.label]
    },
    UPDATE_WORKFLOW_STATE: {
        value: "UPDATE_WORKFLOW_STATE",
        label: "Update Workflow State",
        encounterTypes: [ENCOUNTER_TYPES.CONVERSATION.label]
    }
}

function AddEditRuleVersion({ rule, ruleVersion, onUpdate, surveys, taskTypes, numbers, dispositions, workflows }) {
    const [qualifiers, setQualifiers] = React.useState();
    const [targets, setTargets] = React.useState();
    const [multiTrigger, setMultiTrigger] = React.useState(ruleVersion ? ruleVersion.multipleTrigger : false);
    const [matchWindowAmount, setMatchWindowAmount] = React.useState(0);
    const [selectedMatchWindowUnitOption, setSelectedMatchWindowUnitOption] = React.useState(null);
    const [editorState, setEditorState] = React.useState(() => EditorState.createEmpty());
    const [libraryOpen, setLibraryOpen] = React.useState();
    const [schema, setSchema] = React.useState([]);
    const [encounterType, setEncounterType] = React.useState(ruleVersion ? ruleVersion.encounterType : "CAD");
    const [typeOptions, setTypeOptions] = React.useState([]);
    const [workflow, setWorkflow] = React.useState(null);
    const [subscribers, setSubscribers] = React.useState([]);
    const [phoneSubscribers, setPhoneSubscribers] = React.useState({});
    // Monotonic counter for assigning a stable client-side key to not-yet-saved targets (which have no id).
    const tempKeyRef = React.useRef(0);
    // In-flight guard for Save. A second (rapid) click would re-serialize stale targets — whose ids the
    // first save's updateVersion has just regenerated — against phoneSubscribers re-keyed to the NEW ids,
    // producing empty targetPhones. A synchronous ref blocks the duplicate click immediately (EN-11101).
    const savingRef = React.useRef(false);
    const [disableButton, setDisableButton] = React.useState(false);
    const [emailSubjectEditor, setEmailSubjectEditor] = React.useState(() => EditorState.createEmpty());
    const [emailBodyEditor, setEmailBodyEditor] = React.useState(() => EditorState.createEmpty());
    const [emailLibraryModal, setEmailLibraryModal] = React.useState({ open: false, emailEditorType: "" });
    const [hasBeenSaved, setHasBeenSaved] = React.useState(false);

    let domEditor, emailSubjectDomEditor, emailBodyDomEditor;

    const setDomEditorRef = ref => domEditor = ref;
    const setEmailSubjectDomEditorRef = emailSubjectRef => emailSubjectDomEditor = emailSubjectRef;
    const setEmailBodyDomEditorRef = emailBodyRef => emailBodyDomEditor = emailBodyRef;

    function editorContainerClick(emailEditorType) {
        if (emailEditorType === "subject") {
            emailSubjectDomEditor.focus();
        } else if (emailEditorType === "body") {
            emailBodyDomEditor.focus();
        } else {
            domEditor.focus();
        }
    }

    const addEmails = (...emails) => {
        const emailList = [...subscribers.concat(emails)];
        setSubscribers(emailList);
    }

    const removeEmail = index => {
        const emails = [...subscribers];
        emails.splice(index, 1);
        setSubscribers(emails);
    }

    // Stable key for a target's phone list. Saved targets use their server id; not-yet-saved targets
    // use a client-side key assigned on first interaction. Keying by id (not render index) prevents the
    // phone list from misaligning when target order/timing shifts on load or "edit as a new version"
    // (EN-11101). phoneKeyFor is a PURE read (safe to call during render): it returns the id-based key,
    // an already-assigned temp key, or undefined. The temp key is only minted in event handlers via
    // ensurePhoneKey, never during render.
    const phoneKeyFor = (target) => {
        if (!target) return undefined;
        if (target.id != null) return `id-${target.id}`;
        return target.__phoneKey;
    }

    // Mutating variant — call ONLY from event handlers (never render). Lazily assigns a stable temp key
    // to an unsaved target so its phone list survives until the target gets a server id.
    const ensurePhoneKey = (target) => {
        if (!target) return undefined;
        if (target.id != null) return `id-${target.id}`;
        if (target.__phoneKey == null) target.__phoneKey = `tmp-${tempKeyRef.current++}`;
        return target.__phoneKey;
    }

    const addPhones = (target, ...phones) => {
        const key = ensurePhoneKey(target);
        setPhoneSubscribers(prev => {
            const existing = prev[key] || [];
            return { ...prev, [key]: [...existing, ...phones] };
        });
    }

    const removePhone = (target, index) => {
        const key = ensurePhoneKey(target);
        setPhoneSubscribers(prev => {
            const existing = [...(prev[key] || [])];
            existing.splice(index, 1);
            return { ...prev, [key]: existing };
        });
    }

    const updateEditorState = (newEditorState, targetIdx, emailEditorType) => {
        // Apply the decorator to ensure variables are highlighted
        const decoratedEditorState = EditorState.set(newEditorState, { decorator: compositeDecorator });

        if (emailEditorType === "subject") {
            setEmailSubjectEditor(decoratedEditorState);
            if (targetIdx !== undefined && targets[targetIdx]) {
                targets[targetIdx].emailSubject = decoratedEditorState.getCurrentContent().getPlainText();
            }
        } else if (emailEditorType === "body") {
            setEmailBodyEditor(decoratedEditorState);
            if (targetIdx !== undefined && targets[targetIdx]) {
                targets[targetIdx].emailBody = decoratedEditorState.getCurrentContent().getPlainText();
            }
        } else {
            setEditorState(decoratedEditorState);
            let idx = _.findIndex(targets, (t) => t.type === TYPE_OPTIONS.SEND_TEXT.value);
            if (idx !== -1) {
                targets[idx].message = decoratedEditorState.getCurrentContent().getPlainText();
            }
        }
        setTargets([...targets]);
    };

    const openLibrary = (emailEditorType) => {
        if (emailEditorType) {
            setEmailLibraryModal({open: true, emailEditorType});
        } else {
            setLibraryOpen(true);
        }
    }

    const closeLibrary = (emailEditorType) => {
        if (emailEditorType) {
            setEmailLibraryModal({ open: false, emailEditorType: "" });
        }else{
            setLibraryOpen(false);
        }
    }

    const onLibrarySelection = (targetIdx, article, emailEditorType) => {
        if (emailEditorType){
            setEmailLibraryModal({ open: false, emailEditorType: "" });
        }
        else{
            setLibraryOpen(false);
        }
        if (targets[targetIdx].type === TYPE_OPTIONS.SEND_EMAIL.value) {
            if (emailEditorType === "subject") {
                targets[targetIdx].emailSubjectLibraryItem = article;
            } else {
                targets[targetIdx].emailBodyLibraryItem = article;
            }
        } else if (targets[targetIdx].type === TYPE_OPTIONS.SEND_TEXT.value) {
            targets[targetIdx].libraryItem = article;
        }
        setTargets([].concat(targets));
    }

    const clearArticle = (targetIdx, emailEditorType) => {
        if (emailEditorType === "subject") {
            targets[targetIdx].emailSubjectLibraryItem = null;
        } else if (emailEditorType === "body") {
            targets[targetIdx].emailBodyLibraryItem = null;
        } else {
            targets[targetIdx].libraryItem = null;
        }
        setTargets([].concat(targets));
    }

    const addFilter = () => {
        let editQuals = qualifiers;
        if (!editQuals) {
            editQuals = [];
        }
        setQualifiers(editQuals.concat({
            standardFieldName: null,
            encounterField: null,
            comparison: null,
            value: null
        }));
    }

    const addTarget = () => {
        let editTargets = targets;
        if (!editTargets) {
            editTargets = [];
        }
        setTargets(editTargets.concat({
            queueSend: true // set this property as a way of defaulting to enabled
        }));
    }

    const loadEncounterSchema = (type) => {
        return API.get('Core', '/api/v1/encounter/schema/' + type)
            .then(response => {
                setSchema(Object.assign([], response));
            },
                error => {
                    toast("Could not load schema: " + error.response.data.message, { position: "top-center", type: "error" });
                }
            );
    }

    const initiateTarget = (target) => {
        target.delayUnit = _.find(timeWindows, function (tw) { return tw.value === EMPTY_VALUE; })
        if (target.sendDelay) {
            if (target.sendDelay % WEEK_MINUTES === 0) {
                target.delayUnit = _.find(timeWindows, function (tw) { return tw.value === "WEEK"; })
                target.delayValue = target.sendDelay / WEEK_MINUTES;
            }
            else if (target.sendDelay % DAY_MINUTES === 0) {
                target.delayUnit = _.find(timeWindows, function (tw) { return tw.value === "DAY"; })
                target.delayValue = target.sendDelay / DAY_MINUTES;
            }
            else if (target.sendDelay % HOUR_MINUTES === 0) {
                target.delayUnit = _.find(timeWindows, function (tw) { return tw.value === "HOUR"; })
                target.delayValue = target.sendDelay / HOUR_MINUTES;
            }
            else if (target.sendDelay % MINUTE_MULTIPLIER === 0) {
                target.delayUnit = _.find(timeWindows, function (tw) { return tw.value === "MINUTE"; })
                target.delayValue = target.sendDelay / MINUTE_MULTIPLIER;
            }
        }
    }

    React.useEffect(() => {
        if (ruleVersion) {
            setEncounterType(ruleVersion.encounterType);
            setMultiTrigger(ruleVersion.multipleTrigger);
            setMatchWindowFromValueInSeconds(ruleVersion.matchWindowInSeconds);
            if (ruleVersion.targets) {
                let hydratedPhoneSubscribers = {};
                _.forEach(ruleVersion.targets, (t) => {
                    initiateTarget(t);
                    if (t.type === TYPE_OPTIONS.SEND_EMAIL.value) {
                        let initEmailSubjectEditorState = ContentState.createFromText(t && t.emailSubject ? t.emailSubject : "");
                        let initEmailBodyEditorState = ContentState.createFromText(t && t.message ? t.message : "");
                        setEmailSubjectEditor(EditorState.createWithContent(initEmailSubjectEditorState, compositeDecorator));
                        setEmailBodyEditor(EditorState.createWithContent(initEmailBodyEditorState, compositeDecorator));
                        setSubscribers(t.targetEmails ? t.targetEmails.split(';') : []);
                    } else if (t.type === TYPE_OPTIONS.SEND_TEXT.value) {
                        let initEditorState = ContentState.createFromText(t && t.message ? t.message : "");
                        setEditorState(EditorState.createWithContent(initEditorState, compositeDecorator));
                        // Key by the target's stable id so the phone list survives reorder/timing skew (EN-11101).
                        hydratedPhoneSubscribers[phoneKeyFor(t)] = t.targetPhones ? t.targetPhones.split(';') : [];
                    }
                });
                setPhoneSubscribers(hydratedPhoneSubscribers);
            } else {
                let initEditorState = ContentState.createFromText("");
                setEditorState(EditorState.createWithContent(initEditorState, compositeDecorator));
                setEmailSubjectEditor(EditorState.createWithContent(initEditorState, compositeDecorator));
                setEmailBodyEditor(EditorState.createWithContent(initEditorState, compositeDecorator));
            }
            setQualifiers(ruleVersion.qualifiers ? ruleVersion.qualifiers : []);
            setTargets(ruleVersion.targets ? ruleVersion.targets : []);

            // Check if this version has been saved before
            // A version is considered saved if it has a version number greater than 0 or if it was activated
            setHasBeenSaved(ruleVersion.version > 0 || ruleVersion.activated);
        }

    }, [ruleVersion])

    const handleTypeOptions = (_targets) => {
        let _typeOptions = Object.values(TYPE_OPTIONS).map(typeOption => {
            typeOption.disabled = typeOption.value === TYPE_OPTIONS.EMPTY.value ? false : (_.findIndex(_targets, (t) => t.type == typeOption.value) != -1)
            typeOption.visible = typeOption.encounterTypes && typeOption.encounterTypes.length && (typeOption.encounterTypes.includes("ALL") || typeOption.encounterTypes.includes(encounterType))
            return typeOption
        });

        _typeOptions = _typeOptions.filter(typeOption => typeOption.visible);

        setTypeOptions(_typeOptions);
    }

    React.useEffect(() => {
        loadEncounterSchema(encounterType);
        handleTypeOptions(targets)
    }, [encounterType]);

    React.useEffect(() => {
        handleTypeOptions(targets)
    }, [targets]);

    const setMatchWindowFromValueInSeconds = (matchWindowInSeconds) => {
        let unit = timeWindows.find((option) => option.value == EMPTY_VALUE);
        let amount = 0;
        if (matchWindowInSeconds) {
            if (matchWindowInSeconds % WEEK_MINUTES === 0) {
                unit = timeWindows.find((option) => option.value === "WEEK");
                amount = matchWindowInSeconds / WEEK_MINUTES;
            } else if (matchWindowInSeconds % DAY_MINUTES === 0) {
                unit = timeWindows.find((option) => option.value === "DAY");
                amount = matchWindowInSeconds / DAY_MINUTES;
            } else if (matchWindowInSeconds % HOUR_MINUTES === 0) {
                unit = timeWindows.find((option) => option.value === "HOUR");
                amount = matchWindowInSeconds / HOUR_MINUTES;
            } else if (matchWindowInSeconds % MINUTE_MULTIPLIER === 0) {
                unit = timeWindows.find((option) => option.value === "MINUTE");
                amount = matchWindowInSeconds / MINUTE_MULTIPLIER;
            }
        }

        setMatchWindowAmount(amount);
        setSelectedMatchWindowUnitOption(unit);
    }

    const setQualifierField = (qualifierIdx, obj, action) => {
        switch (action.action) {
            case "select-option":
                let field = obj.data;

                if (field.baseField)
                    qualifiers[qualifierIdx].standardFieldName = field.key;
                else
                    qualifiers[qualifierIdx].encounterField = field;

                qualifiers[qualifierIdx].value = null;
                setQualifiers([].concat(qualifiers));
                break;
        }
    }

    const setQualifierComparator = (qualifierIdx, obj, action) => {
        switch (action.action) {
            case "select-option":
                qualifiers[qualifierIdx].comparison = obj.value;

                if (["IS_EMPTY", "IS_NOT_EMPTY"].includes(obj.value))
                    qualifiers[qualifierIdx].value = "";

                setQualifiers([].concat(qualifiers));
                break;
        }
    }

    const onValueChange = (qualifierIdx, value) => {
        qualifiers[qualifierIdx].value = value;
        setQualifiers([].concat(qualifiers));
    }

    const cancelEdit = () => {
        onUpdate("version-edit");
    }

    const deleteRuleVersion = () => {
        API.del('Core', '/api/v1/rule/' + rule.id + '/version/' + ruleVersion.id)
            .then(response => {
                onUpdate("version-delete", response);
            },
                error => {
                    toast("Could not delete version: " + error.response.data.message, { position: "top-center", type: "error" });
                }
            );
    }

    const setSendDelay = (target) => {
        if (target.delayUnit && target.delayUnit.value == EMPTY_VALUE) {
            target.sendDelay = null;
        }
        if (target.delayValue && target.delayUnit && target.delayUnit.multiplier) {
            target.sendDelay = target.delayValue * target.delayUnit.multiplier;
        }
    }

    const saveRuleVersion = () => {
        // Ignore a duplicate click while a save is already in flight (see savingRef note above).
        if (savingRef.current) {
            return;
        }

        var valid = true
        for (const target of targets) {
            if (target.delayUnit?.value === 'MINUTE' && (target.delayValue || 0) < MIN_DELAY_MINUTES) {
                valid = false
                break
            }
        }

        if (!valid) {
            toast("Delay from Trigger cannot be less than 5 Minutes ", { position: "top-center", type: "error" });
            return;
        }

        const listComparisons = ["IN_LIST", "NOT_IN_LIST", "IN_LIST_CONTAINS", "NOT_IN_LIST_CONTAINS"];
        for (const qualifier of qualifiers || []) {
            if (listComparisons.includes(qualifier.comparison)) {
                const sizeCheck = validateTotalSize(qualifier.value);
                if (!sizeCheck.valid) {
                    toast(sizeCheck.message, { position: "top-center", type: "error" });
                    return;
                }
            }
        }

        savingRef.current = true;

        ruleVersion.qualifiers = qualifiers;
        ruleVersion.targets = targets;
        ruleVersion.encounterType = encounterType;
        ruleVersion.multipleTrigger = multiTrigger;
        ruleVersion.matchWindowInSeconds = null
        if (selectedMatchWindowUnitOption && selectedMatchWindowUnitOption.value !== EMPTY_VALUE) {
            ruleVersion.matchWindowInSeconds = selectedMatchWindowUnitOption ? matchWindowAmount * selectedMatchWindowUnitOption.multiplier : null
        }
        if (ruleVersion.targets) {
            _.forEach(ruleVersion.targets, function (t) {
                setSendDelay(t);
                if (t.type === TYPE_OPTIONS.SEND_EMAIL.value) {
                    t.targetEmails = subscribers.length > 0 ? subscribers.join(';') : '';
                    t.libraryItem = t.emailBodyLibraryItem || null;
                    // Save the email body content from emailBody field
                    t.message = t.emailBody || "";
                    // Ensure emailSubject is preserved
                    t.emailSubject = t.emailSubject || "";
                } else if (t.type === TYPE_OPTIONS.SEND_TEXT.value) {
                    // Ensure text message is properly saved
                    t.message = t.message || "";
                    // Resolve phones by the target's stable id key (EN-11101).
                    t.targetPhones = (phoneSubscribers[phoneKeyFor(t)] || []).join(';');
                }
            });
        }
        if (ruleVersion.qualifiers) {
            _.forEach(ruleVersion.qualifiers, function (q) {
                if(q.value == null || q.value == ''){
                    const fields = ['encounterDate','callAnswered','callClosed','callDispatched','callReceived']
                    if (fields.includes(q.standardFieldName)){
                        const event = new Date();
                        q.value= event.toISOString();
                    }
                }
            });
        }
        return API.put('Core', '/api/v1/rule/' + rule.id + '/version/' + ruleVersion.id, { body: ruleVersion })
            .then(response => {
                toast("Rule Version Saved!", { position: "top-center", type: "success" });
                setHasBeenSaved(true);
                onUpdate("version-edit", response);
            },
                error => {
                    toast("Could not update version: " + error.response.data.message, { position: "top-center", type: "error" });
                }
            )
            .finally(() => {
                savingRef.current = false;
            });
    }

    const setActive = async () => {
        await saveRuleVersion();
        rule.activeRuleVersion = ruleVersion;
        API.put('Core', '/api/v1/rule/' + rule.id, { body: rule })
            .then(response => {
                onUpdate("set-active", response);
                toast("Successfully Set Rule Version To Active!", {
                    position: "top-center",
                    type: "success"
                });
            },
                error => {
                    toast("Could not set rule active: " + error.response.data.message, {
                        position: "top-center",
                        type: "error"
                    });
                }
            );
    }

    const editNewVersion = (ruleVersion) => {
        API.post('Core', '/api/v1/rule/' + rule.id + "/version/" + ruleVersion.id, { body: rule })
            .then(response => {
                onUpdate("new-version", response);
            },
                error => {
                    toast("Could not set rule active: " + error.response.data.message, { position: "top-center", type: "error" });
                }
            );
    }

    const removeQualifier = (qualifierIdx) => {
        qualifiers.splice(qualifierIdx, 1);
        setQualifiers([].concat(qualifiers));
    }

    const removeTarget = (targetIdx) => {
        // Check if the target being removed is a Send Email type
        const targetToRemove = targets[targetIdx];
        if (targetToRemove && targetToRemove.type === TYPE_OPTIONS.SEND_EMAIL.value) {
            // Clear email editor states to prevent stale data from appearing in new targets
            setSubscribers([]);
            setEmailSubjectEditor(EditorState.createWithContent(ContentState.createFromText(""), compositeDecorator));
            setEmailBodyEditor(EditorState.createWithContent(ContentState.createFromText(""), compositeDecorator));
        } else if (targetToRemove && targetToRemove.type === TYPE_OPTIONS.SEND_TEXT.value) {
            // Clear phone chips for the removed Send Text target (keyed by stable id) to prevent stale data.
            const removedKey = phoneKeyFor(targetToRemove);
            setPhoneSubscribers(prev => {
                const next = { ...prev };
                delete next[removedKey];
                return next;
            });
        }
        targets.splice(targetIdx, 1);
        setTargets([].concat(targets));
    }

    const setTargetType = (targetIdx, obj, action) => {
        switch (action.action) {
            case "select-option":
                targets[targetIdx].type = obj.value;
                if (obj.value === TYPE_OPTIONS.SEND_TEXT.value) {
                    targets[targetIdx].smsConfig = getDoNotReplyNumber();
                    // Set focus to text editor after a short delay to allow render
                    setTimeout(() => {
                        if (domEditor) {
                            domEditor.focus();
                        }
                    }, 100);
                } else if (obj.value === TYPE_OPTIONS.SEND_EMAIL.value) {
                    // Set focus to email subject editor after a short delay to allow render
                    setTimeout(() => {
                        if (emailSubjectDomEditor) {
                            emailSubjectDomEditor.focus();
                        }
                    }, 100);
                }
                setTargets([].concat(targets));
                break;
        }
    }
    const setTargetSurvey = (targetIdx, obj, action) => {
        switch (action.action) {
            case "select-option":
                targets[targetIdx].surveyDefinition = obj.data;
                setTargets([].concat(targets));
                break;
        }
    }

    const setTargetTaskType = (targetIdx, obj, action) => {
        switch (action.action) {
            case "select-option":
                targets[targetIdx].taskType = obj.data;
                setTargets([].concat(targets));
                break;
        }
    }

    const setTargetDisposition = (targetIdx, obj, action) => {
        switch (action.action) {
            case "select-option":
                targets[targetIdx].conversationDisposition = obj.data;
                setTargets([].concat(targets));
                break;
        }
    }

    const setTargetWorkflow = (targetIdx, obj, action) => {
        switch (action.action) {
            case "select-option":
                setWorkflow(obj.data);
                targets[targetIdx].workflowState = null;
                setTargets([].concat(targets));
                break;
        }
    }

    const setTargetWorkflowState = (targetIdx, obj, action) => {
        switch (action.action) {
            case "select-option":
                targets[targetIdx].workflowState = obj.data;
                setTargets([].concat(targets));
                break;
        }
    }

    const toggleTargetReview = (targetIdx) => {
        targets[targetIdx].queueSend = !targets[targetIdx].queueSend;
        setTargets([].concat(targets));
    }

    const setTimeUnit = (targetIdx, obj, action) => {
        switch (action.action) {
            case "select-option":
                targets[targetIdx].delayUnit = obj;
                setTargets([].concat(targets));
                break;
        }
    }

    const setTimeVal = (targetIdx, e) => {
        targets[targetIdx].delayValue = e.target.value;
    }

    const setQuietTimeStartVal = (targetIdx, e) => {
        targets[targetIdx].quietTimeStart = e.target.value;
    }

    const setQuietTimeEndVal = (targetIdx, e) => {
        targets[targetIdx].quietTimeEnd = e.target.value;
    }

    const findPlaceholder = (contentBlock, callback) => {
        const PLACEHOLDER_REGEX = /{{[a-zA-Z]*}}/g;
        const text = contentBlock.getText();
        let matchArr, start;
        while ((matchArr = PLACEHOLDER_REGEX.exec(text)) !== null) {
            start = matchArr.index;
            callback(start, start + matchArr[0].length);
        }
    }

    const VariablePlaceholder = (props) => {
        let varKey = props.decoratedText.replace("{{", "");
        varKey = varKey.replace("}}", "");
        return (
            <span className="variable-placeholder inputVariableTag" {...props}>{props.children}</span>
        )
    }

    const compositeDecorator = new CompositeDecorator([
        {
            strategy: findPlaceholder,
            component: VariablePlaceholder,
        }
    ]);

    const setTargetNumber = (targetIdx, obj, action) => {
        switch (action.action) {
            case "select-option":
                targets[targetIdx].smsConfig = obj;
                setTargets([].concat(targets));
                break;
        }
    }

    const onEncounterTypeChange = (obj, action) => {
        switch (action.action) {
            case "select-option":
                setEncounterType(obj.key);
                break;
        }
    }

    const setTargetRole = (targetIdx, obj, action) => {
        let tr = [];
        if (obj != null) {
            obj.forEach(function (r) {
                tr.push(r.value);
            })
        }
        targets[targetIdx].targetRoles = tr;
        setTargets([].concat(targets));
    }

    const getDoNotReplyNumber = () => {
        return _.find(numbers, function (n) { return n.smsConfigType == "DONOTREPLY"; });
    }

    const addVariableToText = (schema, emailEditorType) => {
        let currentState, setStateFn, targetIdx;
        if (emailEditorType === "subject") {
            currentState = emailSubjectEditor;
            setStateFn = setEmailSubjectEditor;
            targetIdx = _.findIndex(targets, (t) => t.type === TYPE_OPTIONS.SEND_EMAIL.value);
        } else if (emailEditorType === "body") {
            currentState = emailBodyEditor;
            setStateFn = setEmailBodyEditor;
            targetIdx = _.findIndex(targets, (t) => t.type === TYPE_OPTIONS.SEND_EMAIL.value);
        } else {
            currentState = editorState;
            setStateFn = setEditorState;
            targetIdx = _.findIndex(targets, (t) => t.type === TYPE_OPTIONS.SEND_TEXT.value);
        }

        const selectionState = currentState.getSelection();
        const contentState = currentState.getCurrentContent();
        const variableText = `{{${schema.key}}}`;

        // Insert the variable text at the current cursor position
        const contentStateWithEntity = contentState.createEntity("TOKEN", "IMMUTABLE", {time: new Date().getTime()});
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        const newContentState = Modifier.insertText(
            contentStateWithEntity, selectionState, variableText, null, entityKey
        );

        // Get the current cursor position and calculate the new position after the variable
        const currentOffset = selectionState.getAnchorOffset();
        const newOffset = currentOffset + variableText.length;
        const blockKey = selectionState.getAnchorKey();

        // Create new selection at the end of the inserted variable
        const newSelection = selectionState.merge({
            anchorKey: blockKey,
            anchorOffset: newOffset,
            focusKey: blockKey,
            focusOffset: newOffset,
            isBackward: false
        });

        // Push the new content state and apply the selection
        let newEditorState = EditorState.push(currentState, newContentState, 'insert-characters');
        newEditorState = EditorState.forceSelection(newEditorState, newSelection);

        // Apply the decorator to ensure variables are highlighted in blue
        newEditorState = EditorState.set(newEditorState, { decorator: compositeDecorator });

        setStateFn(newEditorState);

        // Update the targets array with the new content
        if (targetIdx !== -1) {
            if (emailEditorType === "subject") {
                targets[targetIdx].emailSubject = newEditorState.getCurrentContent().getPlainText();
            } else if (emailEditorType === "body") {
                targets[targetIdx].emailBody = newEditorState.getCurrentContent().getPlainText();
            } else {
                targets[targetIdx].message = newEditorState.getCurrentContent().getPlainText();
            }
            setTargets([...targets]);
        }
    }

    const toggleMultipleTrigger = () => {
        if (!disabled) {
            if (multiTrigger) {
                setMultiTrigger(false);
            } else {
                setMultiTrigger(true);
            }
        }
    }

    const getSchemaOpts = () => {
        return [
            {
                label: "Base Encounter Fields",
                options: _.concat([{ value: EMPTY_VALUE, label: EMPTY_VALUE }], _.sortBy(_.map(_.filter(schema, (sc) => !!sc.base), (sc) => { return { value: sc.key, label: sc.name, data: sc } }), (schemaOpt) => schemaOpt.label))
            },
            {
                label: "Detailed Fields",
                options: _.sortBy(_.map(_.filter(schema, (sc) => !sc.base), (sc) => { return { value: sc.key, label: sc.name, data: sc } }), (schemaOpt) => schemaOpt.label)
            }
        ];
    }

    const onMatchWindowUnitChange = (option) => {
        setSelectedMatchWindowUnitOption(option);
    }

    let disabled = ruleVersion.activated;

    return (
        <div className="scrollView">
            <div className="navBar">
                <div className="title">Versions</div>
                <div className="separator" />
            </div>
            <div className={"editContent tableView"}>
                <div className={"tableSection filters"}>
                    <div className={"sectionTitle"}>Encounter Type</div>
                    <div className={"sectionRow"}>
                        <EncounterTypePicker value={encounterType} onChange={onEncounterTypeChange} disabled={disabled} />
                    </div>
                </div>

                <div className="tableSection filters">
                    <div className="sectionTitle">Filters</div>
                    {
                        _.map(qualifiers, function (q, i) {
                            let selectedSchema = _.find(schema, (sche) => {
                                let found = false;

                                if (sche.baseField) {
                                    found = sche.key == q.standardFieldName
                                } else if (q.encounterField != null) {
                                    found = sche.id == q.encounterField.id;
                                }

                                return found;
                            });
                            let schemaOpts = getSchemaOpts();
                            let schemaKey = selectedSchema ? selectedSchema.key : EMPTY_VALUE;
                            let flatSchemaOpts = _.flatten(_.map(schemaOpts, function (so) { return so.options; }));
                            let schemaVal = _.find(flatSchemaOpts, function (so) { return so.value == schemaKey; })
                            let compOpts = selectedSchema ? _.map(selectedSchema.validOperators, function (vo) { return { value: vo, label: COMPARISON_MAP[vo] } }) : [];
                            let compVal = _.find(compOpts, function (c) { return c.value == q.comparison; });
                            return (
                                <div className="sectionRow" key={i}>
                                    <Select className="tableCell selectCell schemaSelect" classNamePrefix="selectCell" isDisabled={disabled} value={schemaVal} onChange={setQualifierField.bind(this, i)} options={schemaOpts} />
                                    <Select className="tableCell selectCell comparisonSelect" classNamePrefix="selectCell" isDisabled={disabled || !selectedSchema} value={compVal} onChange={setQualifierComparator.bind(this, i)} options={compOpts} />
                                    {
                                        selectedSchema ? (
                                            <ComparisonFieldValue disabled={disabled} value={q.value} onChange={onValueChange.bind(this, i)} qualifier={q} type={selectedSchema.type} fieldName={selectedSchema.key} />
                                        ) : null
                                    }
                                    <div xs={1}>
                                        <div className={"remove"} onClick={removeQualifier.bind(this, i)}>
                                            <div className="icon" />
                                        </div>
                                    </div>
                                </div>
                            )
                        })
                    }
                    {
                        disabled ? null : (
                            <div onClick={addFilter} className="button tint medium">
                                <div className="title">Add Filter</div>
                            </div>
                        )
                    }
                </div>

                <div className="tableSection target">
                    <div className="sectionTitle">Target</div>
                    {
                        _.map(
                            _.sortBy(targets, t => t.id), // Sort targets by id to maintain consistent order
                            (t, j) => {
                            let typeKey = t ? t.type : EMPTY_VALUE;
                            let typeVal = _.find(typeOptions, (to) => to.value == typeKey);
                            let body = (
                                <div className="tableCell disabled placeholder">
                                    <div className="title">Select a Target Type</div>
                                </div>
                            );

                            switch (typeKey) {
                                case TYPE_OPTIONS.SEND_SURVEY.value:
                                    let surveyOpts = _.concat([{ value: EMPTY_VALUE, label: EMPTY_VALUE }], _.map(surveys, (sc) => { return { value: sc.id, label: sc.name, data: sc } }));
                                    let surveyKey = t && t.surveyDefinition ? t.surveyDefinition.id : EMPTY_VALUE;
                                    let surveyVal = _.find(surveyOpts, (sv) => sv.value == surveyKey)
                                    body = (
                                        <Select className="selectCell tableCell surveySelect" classNamePrefix="selectCell" isDisabled={disabled}
                                            value={surveyVal} onChange={setTargetSurvey.bind(this, j)} options={surveyOpts}
                                        />
                                    );
                                    break;
                                case TYPE_OPTIONS.CREATE_TASK.value:
                                    let taskOpts = _.concat([{ value: EMPTY_VALUE, label: EMPTY_VALUE }], _.map(taskTypes, (tt) => { return { value: tt.id, label: tt.name, data: tt } }));
                                    let taskKey = t && t.taskType ? t.taskType.id : EMPTY_VALUE;
                                    let taskVal = _.find(taskOpts, (t) => t.value == taskKey)
                                    body = (
                                        <Select className="selectCell tableCell surveySelect" classNamePrefix="selectCell" isDisabled={disabled}
                                            value={taskVal} onChange={setTargetTaskType.bind(this, j)} options={taskOpts}
                                        />
                                    );
                                    break;
                                case TYPE_OPTIONS.SEND_TEXT.value:
                                    const styleMap = {
                                        'STRIKETHROUGH': {
                                            textDecoration: 'line-through',
                                        },
                                    };
                                    let libraryPicker = (
                                        <Modal
                                            button={(
                                                <div style={{ position: 'absolute', right: '15px', bottom: '12px' }} className="selectFromLibrary button small">
                                                    <div className="buttonTitle">Or Select from Library</div>
                                                </div>)}
                                            content={(
                                                <LibraryPicker
                                                    defaultArticleGroupId={t.libraryItem  ? t.libraryItem.libraryGroup.id : null}
                                                    defaultArticleId={t.libraryItem ? t.libraryItem.id : null}
                                                    onSelect={(article) => onLibrarySelection(j, article)}
                                                    onCancel={() => closeLibrary()}/>
                                            )}
                                            title="Select Article from Library"
                                            size="xl"
                                            handleClose={() => closeLibrary()}
                                            handleOpen={() => openLibrary()}
                                            open={!!libraryOpen}
                                        />
                                    )

                                    body = (
                                        <div>
                                            {
                                                // If an article is selected then show contents of article, otherwise show editor
                                                t.libraryItem ? (
                                                    <div className="editorCell tableCell targetTextMessage">
                                                        <Editor
                                                            readOnly={true}
                                                            editorState={EditorState.createWithContent(ContentState.createFromText(t.libraryItem.contents))}
                                                            onChange={() => { }}
                                                            customStyleMap={styleMap}
                                                        />
                                                        <div>
                                                            <div style={{ position: 'absolute', left: '15px', bottom: '12px' }} onClick={() => clearArticle(j)} className="selectFromLibrary button small">
                                                                <div className="buttonTitle">Switch to Ad-Hoc Message</div>
                                                            </div>
                                                            {libraryPicker}
                                                        </div>
                                                    </div>

                                                ) : (
                                                    <div className="editorCell tableCell targetTextMessage" onClick={() => editorContainerClick()}>
                                                        <Editor
                                                            readOnly={disabled}
                                                            editorState={editorState}
                                                            customStyleMap={styleMap}
                                                            onChange={updateEditorState}
                                                            placeholder={"Click and type to build template for text message..."}
                                                            spellCheck={true}
                                                            stripPastedStyles={true}
                                                            ref={setDomEditorRef}
                                                        />
                                                        {
                                                            disabled ? null : (
                                                                <div>
                                                                    <Popup position={"bottom center"} trigger={(
                                                                        <div className="inputAction serviceOffering">
                                                                            <div className="addVariable button small tint">
                                                                                <div className="buttonTitle">Add Variable</div>
                                                                            </div>
                                                                        </div>
                                                                    )}>
                                                                        <div style={{ height: '250px', overflow: 'scroll' }} className={"serviceOffering pullDownMenu"}>
                                                                            {_.map(_.sortBy(schema, (s) => s.name?.toLowerCase()), (s, k) =>  {
                                                                                    return (
                                                                                        <div key={k} onClick={addVariableToText.bind(this, s)} className={"cell detailed hasIcon"}>
                                                                                            <div className="cellBody" style={{ paddingLeft: "10px" }}>
                                                                                                <div className="title serviceName">{s.name}</div>
                                                                                            </div>
                                                                                        </div>
                                                                                    )
                                                                                })
                                                                            }
                                                                        </div>
                                                                    </Popup>
                                                                    <div>
                                                                        {libraryPicker}
                                                                    </div>
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                )
                                            }
                                            <div className="tableCell titledSelectCell targetResponseGroup">
                                                <div className="title">Communication Group</div>
                                                <Select className="selectCell responseGroupSelect" classNamePrefix="selectCell"
                                                    isDisabled={disabled}
                                                    onChange={setTargetNumber.bind(this, j)}
                                                    value={t.smsConfig ? t.smsConfig : getDoNotReplyNumber()}
                                                    options={numbers}
                                                    getOptionLabel={option => option.name}
                                                    getOptionValue={option => option.id}
                                                />
                                            </div>
                                        </div>
                                    )
                                    break;
                                case TYPE_OPTIONS.SEND_EMAIL.value:
                                    const emailStyleMap = {
                                        'STRIKETHROUGH': {
                                            textDecoration: 'line-through',
                                        },
                                    };
                                    const renderEditorBlock = (emailEditorType, editorState, libraryItem, j, placeholder, ref) => {
                                        const libraryPicker = (
                                            <Modal
                                                button={(
                                                    <div style={{position: 'absolute', right: '15px', bottom: '12px'}}
                                                         className="selectFromLibrary button small">
                                                        <div className="buttonTitle">Or Select from Library</div>
                                                    </div>)}
                                                content={(
                                                    <LibraryPicker
                                                        defaultArticleGroupId={libraryItem ? libraryItem.libraryGroup.id : null}
                                                        defaultArticleId={libraryItem ? libraryItem.id : null}
                                                        onSelect={(article) => onLibrarySelection(j, article, emailEditorType)}
                                                        onCancel={() => closeLibrary(emailEditorType)}/>
                                                )}
                                                title="Select Article from Library"
                                                size="xl"
                                                handleClose={() => closeLibrary(emailEditorType)}
                                                handleOpen={() => openLibrary(emailEditorType)}
                                                open={emailLibraryModal.open && emailLibraryModal.emailEditorType === emailEditorType}
                                            />
                                        );


                                        return (
                                            <div style={{marginBottom: "10px"}}>
                                                {
                                                    libraryItem ? (
                                                        <div aria-label={`Email${emailEditorType} library editor`} className="editorCell tableCell targetTextMessage">
                                                            <Editor
                                                                readOnly={true}
                                                                editorState={EditorState.createWithContent(ContentState.createFromText(libraryItem.contents))}
                                                                onChange={() => {
                                                                }}
                                                                customStyleMap={emailStyleMap}
                                                            />
                                                            <div>
                                                                <div
                                                                    style={{
                                                                        position: 'absolute',
                                                                        left: '15px',
                                                                        bottom: '12px'
                                                                    }}
                                                                    onClick={() => clearArticle(j, emailEditorType)}
                                                                    className="selectFromLibrary button small"
                                                                >
                                                                    <div className="buttonTitle">Switch to Ad-Hoc
                                                                        Message
                                                                    </div>
                                                                </div>
                                                                {libraryPicker}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div aria-label={`Email${emailEditorType} editor`} className="editorCell tableCell targetTextMessage"
                                                             onClick={() => editorContainerClick(emailEditorType)}>
                                                            <Editor
                                                                readOnly={disabled}
                                                                editorState={editorState}
                                                                customStyleMap={emailStyleMap}
                                                                onChange={(newState) => updateEditorState(newState, j, emailEditorType)}
                                                                placeholder={placeholder}
                                                                spellCheck={true}
                                                                stripPastedStyles={true}
                                                                ref={ref}
                                                            />
                                                            {
                                                                disabled ? null : (
                                                                    <div>
                                                                        <Popup position={"bottom center"} trigger={(
                                                                            <div
                                                                                className="inputAction serviceOffering">
                                                                                <div
                                                                                    className="addVariable button small tint">
                                                                                    <div className="buttonTitle">Add
                                                                                        Variable
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}>
                                                                            <div style={{
                                                                                height: '250px',
                                                                                overflow: 'scroll'
                                                                            }}
                                                                                 className={"serviceOffering pullDownMenu"}>
                                                                                {_.map(_.sortBy(schema, (s) => s.name?.toLowerCase()), (s, k) =>  (
                                                                                    <div
                                                                                        key={k}
                                                                                        onClick={() => addVariableToText(s, emailEditorType)}
                                                                                        className={"cell detailed hasIcon"}
                                                                                    >
                                                                                        <div className="cellBody"
                                                                                             style={{paddingLeft: "10px"}}>
                                                                                            <div
                                                                                                className="title serviceName">{s.name}</div>
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </Popup>
                                                                        <div>{libraryPicker}</div>
                                                                    </div>
                                                                )
                                                            }
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        );
                                    };

                                    body = (
                                        <div>
                                            {renderEditorBlock("subject", emailSubjectEditor, t.emailSubjectLibraryItem, j, "Click and type to build template for email subject line...", setEmailSubjectDomEditorRef)}
                                            {renderEditorBlock("body", emailBodyEditor, t.emailBodyLibraryItem, j, "Click and type to build template for email message...", setEmailBodyDomEditorRef)}
                                        </div>
                                    );
                                    break;
                                case TYPE_OPTIONS.UPDATE_DISPOSITION.value:
                                    let dispositionsOpts = _.concat([{ value: EMPTY_VALUE, label: EMPTY_VALUE }], _.map(dispositions, (d) => { return { value: d.id, label: d.name, data: d } }));
                                    let dispositionKey = t && t.conversationDisposition ? t.conversationDisposition.id : EMPTY_VALUE;
                                    let dipositionVal = _.find(dispositionsOpts, (d) => d.value == dispositionKey)
                                    body = (
                                        <Select className="selectCell tableCell surveySelect" classNamePrefix="selectCell" isDisabled={disabled}
                                            value={dipositionVal} onChange={setTargetDisposition.bind(this, j)} options={dispositionsOpts}
                                        />
                                    );
                                    break;
                                case TYPE_OPTIONS.UPDATE_WORKFLOW_STATE.value:

                                    let workflowsOpts = _.concat([{ value: EMPTY_VALUE, label: EMPTY_VALUE }], _.map(workflows, (w) => { return { value: w.id, label: w.name, data: w } }));

                                    let workflowStates = workflow && workflows.find(w => w.id === workflow.id) ? workflows.find(w => w.id === workflow.id).states : []
                                    let workflowsStatesOpts = _.concat([{ value: EMPTY_VALUE, label: EMPTY_VALUE }], _.map(workflowStates, (ws) => { return { value: ws.id, label: ws.name, data: ws } }));
                                    let workflowStateKey = EMPTY_VALUE;

                                    if (t && t.workflowState && t.workflowState.id) {
                                        workflowStateKey = t.workflowState.id;
                                        if (!workflow && workflows && workflows.length) {
                                            let selectedWorkflow = workflows.filter(w => w.states.find(ws => ws.id === t.workflowState.id))
                                            if (selectedWorkflow && selectedWorkflow.length)
                                                setWorkflow(selectedWorkflow[0])
                                        }
                                    }
                                    let workflowStateVal = _.find(workflowsStatesOpts, (ws) => ws.value == workflowStateKey)

                                    body = (
                                        <div>
                                            <div className="tableCell titledSelectCell targetResponseGroup">
                                                <div className="title">Workflow</div>
                                                <Select className="selectCell tableCell surveySelect" classNamePrefix="selectCell" isDisabled={disabled}
                                                    value={workflow && workflow !== EMPTY_VALUE ? { value: workflow.id, label: workflow.name, data: workflow } : null} onChange={setTargetWorkflow.bind(this, j)} options={workflowsOpts}
                                                />
                                            </div>
                                            {workflow &&
                                                <div className="tableCell titledSelectCell targetResponseGroup">
                                                    <div className="title">State</div>
                                                    <Select className="selectCell tableCell surveySelect" classNamePrefix="selectCell" isDisabled={disabled}
                                                        value={workflowStateVal} onChange={setTargetWorkflowState.bind(this, j)} options={workflowsStatesOpts}
                                                    />
                                                </div>
                                            }
                                        </div>
                                    );
                                    break;
                            }

                            return (
                                <div className={"targetListItem"} key={j}>
                                    <div className={"targetContainer targetCell"}>
                                        <div className="targetBody">
                                            {body}
                                            {
                                                typeVal ? (
                                                    <div>
                                                        <div className={`tableCell titledSelectCell delayFromTrigger`}>
                                                            <div className="title">Delay from Trigger</div>
                                                            {
                                                                t.delayUnit && t.delayUnit.value == EMPTY_VALUE ? null :
                                                                    <InputCell
                                                                        disabled={disabled}
                                                                        required
                                                                        type={"number"}
                                                                        defaultValue={t.delayValue}
                                                                        onChange={setTimeVal.bind(this, j)}
                                                                        value={t.delayValue}
                                                                    />
                                                            }
                                                            <Select className="tableCell selectCell" value={t.delayUnit} classNamePrefix="selectCell" isDisabled={disabled} onChange={setTimeUnit.bind(this, j)} options={timeWindows} />
                                                        </div>
                                                        <div onClick={toggleTargetReview.bind(this, j)} className={`tableCell switchCell ${t.queueSend ? 'switchOn' : 'switchOff'}`}>
                                                            <div className="title">Require Review</div>
                                                            <div className={`accessory accessorySwitch ${t.queueSend ? 'on' : 'off'}`}>
                                                                <div className="switchThumb" />
                                                            </div>
                                                        </div>
                                                        {t.type == TYPE_OPTIONS.SEND_EMAIL.value ? (<div
                                                            className="tableCell sendEmail  titledSelectCell">
                                                            <div className="cellBody">
                                                                <div className="title">Target Emails</div>
                                                                <div className="input">
                                                                    <EmailChips
                                                                        emailsList={subscribers}
                                                                        addEmails={addEmails}
                                                                        removeItem={removeEmail}
                                                                        onEmailInput={() => setDisableButton(true)}
                                                                        onEmailEmpty={() => setDisableButton(false)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>) : (
                                                            <div
                                                                className="tableCell titledSelectCell targetResponseGroup">
                                                                <div className="title">Target Role</div>
                                                                <Select className="selectCell responseGroupSelect"
                                                                        classNamePrefix="selectCell"
                                                                        isDisabled={disabled}
                                                                        isMulti
                                                                        onChange={setTargetRole.bind(this, j)}
                                                                        value={t.targetRoles != null && t.targetRoles.length > 0 ? t.targetRoles.map(function (o) {
                                                                            return {label: o, value: o}
                                                                        }) : ''}
                                                                        options={roles}
                                                                        menuPortalTarget={document.body}
                                                                        styles={{
                                                                            menuPortal: base => ({
                                                                                ...base,
                                                                                zIndex: 9999
                                                                            }), menu: base => ({...base, color: 'black'})
                                                                        }}
                                                                />
                                                            </div>)
                                                        }
                                                        {t.type === TYPE_OPTIONS.SEND_TEXT.value ? (
                                                            <div className="tableCell sendEmail  titledSelectCell">
                                                                <div className="cellBody">
                                                                    <div className="title">Target Phone Number</div>
                                                                    <div className="input">
                                                                        <PhoneChips
                                                                            phonesList={phoneSubscribers[phoneKeyFor(t)] || []}
                                                                            addPhones={(...p) => addPhones(t, ...p)}
                                                                            removeItem={(i) => removePhone(t, i)}
                                                                            inputAriaLabel="Target Phone Number"
                                                                            onPhoneInput={() => setDisableButton(true)}
                                                                            onPhoneEmpty={() => setDisableButton(false)}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : null}
                                                        <div className={`tableCell titledSelectCell delayFromTrigger`}>
                                                            <div className="title">Quiet Hours: </div>
                                                            <InputCell
                                                                disabled={disabled}
                                                                type={"time"}
                                                                defaultValue={t.quietTimeStart != null ? t.quietTimeStart : "--:--"}
                                                                onChange={setQuietTimeStartVal.bind(this, j)}
                                                                value={t.quietTimeStart}
                                                                placeholder={"--:--"}
                                                            />
                                                            <div className="title"> to: </div>
                                                            <InputCell
                                                                disabled={disabled}
                                                                type={"time"}
                                                                defaultValue={t.quietTimeEnd != null ? t.quietTimeEnd : "--:--"}
                                                                onChange={setQuietTimeEndVal.bind(this, j)}
                                                                value={t.quietTimeEnd}
                                                                placeholder={"--:--"}
                                                            />
                                                        </div>
                                                    </div>
                                                ) : null
                                            }
                                        </div>

                                        <Select aria-label="target types" isOptionDisabled={(option) => option.disabled}
                                                className="targetType tableCell selectCell targetSelect"
                                                classNamePrefix="selectCell" isDisabled={disabled} value={typeVal}
                                                onChange={setTargetType.bind(this, j)} options={typeOptions}
                                                menuPortalTarget={document.body}
                                                styles={{menuPortal: base => ({...base, zIndex: 9999}), menu: base => ({...base, color: 'black'})}}
                                                ariaLabelledBy="target types"
                                                formatOptionLabel={(option) => (
                                                    <span
                                                        aria-label={`target type option ${option.label}`}>{option.label}</span>
                                                )}/>
                                    </div>
                                    <div className={"removeTarget"} onClick={removeTarget.bind(this, j)}>
                                        <div className="icon" />
                                    </div>
                                </div>

                            )
                        })
                    }
                    {
                        disabled ? null : (
                            <div onClick={addTarget} className="button tint medium">
                                <div className="title">Add Target</div>
                            </div>
                        )
                    }
                </div>

                <div onClick={toggleMultipleTrigger} className={`tableCell switchCell ${multiTrigger ? 'switchOn' : 'switchOff'}`}>
                    <div className="title">Allow Multiple Triggers on Encounter Update</div>
                    <div className={`accessory accessorySwitch ${multiTrigger ? 'on' : 'off'}`}>
                        <div className="switchThumb" />
                    </div>
                </div>

                <div className={`tableCell titledSelectCell matchwindowRule`}>
                    <div className="title">Match Window </div>

                    {
                        selectedMatchWindowUnitOption && selectedMatchWindowUnitOption.value === EMPTY_VALUE ? null :
                            <InputCell
                                disabled={disabled}
                                required
                                type={"number"}
                                defaultValue={(matchWindowAmount ? matchWindowAmount : "0")}
                                onChange={(e) => setMatchWindowAmount(e.target.value)}
                                value={matchWindowAmount} />
                    }

                    <Select className="tableCell selectCell" value={selectedMatchWindowUnitOption} classNamePrefix="selectCell"
                        isDisabled={disabled}
                        onChange={onMatchWindowUnitChange} options={timeWindows} />
                </div>


            </div>
            <div className="actionBar">
                <div className="actions versioning left">
                    {
                        (rule.activeRuleVersion && rule.activeRuleVersion.id == ruleVersion.id) ?
                            (
                                <div className={`button tint medium`}>
                                    <div className="title">Active</div>
                                </div>
                            ) :
                            (
                                <div onClick={setActive} className={`button tint medium`}>
                                    <div className="title">Set as Active</div>
                                </div>
                            )
                    }
                    {
                        (!ruleVersion.activated && hasBeenSaved) ?
                            (
                                <div onClick={deleteRuleVersion} className="button destructive medium">
                                    <div className="title">Delete</div>
                                </div>
                            ) : null
                    }
                </div>


                {
                    !ruleVersion.activated ? (
                        <div className="actions edit right">
                            <div onClick={cancelEdit} className="button dismiss medium">
                                <div className="title">Cancel</div>
                            </div>
                            <div onClick={saveRuleVersion} className={`button confirm medium`}>
                                <div className="title">Save</div>
                            </div>
                        </div>
                    ) : (
                        <div className="actions edit right">
                            <div onClick={editNewVersion.bind(this, ruleVersion)} className={`button plain medium`}>
                                <div className="title">Edit as a New Version</div>
                            </div>
                        </div>
                    )
                }
                <div className="separator"></div>
            </div>
        </div>
    )
}

export default AddEditRuleVersion;